import React, { FC, Fragment, useState } from 'react';
import { compose } from 'recompose';
import { MutationFunction } from 'react-apollo';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Hidden, Typography } from '@material-ui/core';
import { Trans } from 'react-i18next';
import i18next from 'i18next';

import {
  GetFollowedDocument,
  UpdateUserProfileComponent,
  UserProfileInput,
  useUpdateUserProfileOnboardingMutation,
} from 'api';
import { withNotify, IWithNotify, withFeatures, IWithFeatures } from 'providers';
import { ClientLogo, Layouts } from 'components';
import { getFullName } from 'helpers';
import * as paths from 'paths';
import { useCurrentUser } from 'hooks';
import { FOLLOW_QUERIES_LIMIT } from 'constants/index';

import { Form, ImageUpload, Main } from '../components';

interface IPropsEnhanced extends IWithNotify, IWithFeatures, RouteComponentProps {}

const FORM_HEIGHT = 450;

const enhancers = compose<IPropsEnhanced, {}>(withNotify, withRouter, withFeatures);

const Profile: FC<IPropsEnhanced> = ({ features, history, notify }) => {
  const [working, setWorking] = useState(false);
  const currentUser = useCurrentUser();
  const [updateUserProfileOnboardingMutation] = useUpdateUserProfileOnboardingMutation({
    onCompleted: () => {
      setWorking(false);
      history.push(paths.index);
    },
  });

  function handleCompleted() {
    if (features.skipOnboardingSelectGroups) {
      updateUserProfileOnboardingMutation({
        variables: {
          data: {
            onboarding_completed: true,
            last_onboarding_step: 2,
          },
        },
        refetchQueries: [
          {
            query: GetFollowedDocument,
            variables: {
              data: {
                user_id: currentUser.id,
                limit: FOLLOW_QUERIES_LIMIT,
              },
            },
          },
        ],
      });
    } else {
      setWorking(false);
      history.push(paths.onboardingFollows);
    }
  }

  function handleError() {
    setWorking(false);
    notify.mutationError();
  }

  function createOnSubmit(updateUserProfile: MutationFunction) {
    return (values: UserProfileInput) => {
      const { department_id, location_id, ...rest } = values;

      const variables = {
        data: {
          last_onboarding_step: 1,
          lang: i18next.language,
          department_id: department_id ? department_id : null,
          location_id: location_id ? location_id : null,
          ...rest,
        },
      };

      window.Intercom?.('update', {
        id: values.id,
        name: getFullName(values),
        job_title: values.job_title,
      });

      setWorking(true);

      return updateUserProfile({ variables });
    };
  }

  function renderSidebar() {
    return (
      <UpdateUserProfileComponent onError={handleError} onCompleted={handleCompleted}>
        {updateUserProfile => (
          <Fragment>
            <ClientLogo />
            <Typography color="textSecondary" gutterBottom variant="h4">
              <Trans i18nKey="onboarding:sidebarProfileHeader" />
            </Typography>
            <ImageUpload />
            <div style={{ minHeight: FORM_HEIGHT }}>
              <Form onSubmit={createOnSubmit(updateUserProfile)} working={working} />
            </div>
          </Fragment>
        )}
      </UpdateUserProfileComponent>
    );
  }

  return (
    <Layouts.Full2Column
      sidebar={renderSidebar()}
      main={
        <Hidden xsDown>
          <Main />
        </Hidden>
      }
    />
  );
};

export default enhancers(Profile);
